import { useCallback, useEffect, useRef, useState } from "react";
import { useMeContext } from "../../MeApp";
import { StepShell, StepHeading } from "@codel/dova-ds";
import { Button } from "../components/Button";
import { CouplePair } from "../components/CouplePair";
import { FirstSessionModule } from "../components/FirstSessionModule";
import type { StepProps } from "../flowConfig";

/** How long the screen takes to leave. `.dova-sink` runs for --dur-slow, and
 *  this waits for it, so the number is the token's and not a second opinion. */
const LEAVE_MS = 400;

/** The entrance beat, 60ms, the same cadence the therapist splash staggers on.
 *
 *  `.dova-rise` fills `both`, so a delayed beat holds its `from` (opacity 0)
 *  instead of flashing at full opacity and then animating. It does NOT gate
 *  interaction: the button is mounted and pressable through its own delay, so
 *  someone who has seen this screen before can press straight through. Under
 *  prefers-reduced-motion `.dova-rise` does not apply and the delays are inert,
 *  so all three beats are simply there. */
const BEAT_MS = 60;

export function S1Welcome({ onNext }: StepProps) {
  const { couple } = useMeContext();
  const [leaving, setLeaving] = useState(false);
  // Synchronous twin of `leaving`. `leaving` is React state, so it is not
  // readable until the next render — two taps in the same tick both see the
  // pre-update `false` and both schedule an advance. This ref is written
  // immediately, so the second tap's guard reads the first tap's write.
  // `leaving` itself stays exactly as it was: it still drives the animation.
  const leavingRef = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  // Advancing replaces this component; a pending timer firing into a dead tree
  // is a React warning at best.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // THE HANDOVER. The screen leaves as one movement and the first question
  // arrives after it, rather than the two swapping on the same frame.
  //
  // It leaves as ONE, having entered in three: a departure that reads as three
  // separate departures is fussy where a staggered arrival is considered. Same
  // split the therapist splash makes.
  //
  // Reduced motion means `.dova-sink` never runs, so there is nothing to wait
  // for. Waiting anyway would leave a motion-averse partner looking at a frozen
  // screen for 400ms after pressing the button.
  const start = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onNext();
      return;
    }
    setLeaving(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(onNext, LEAVE_MS);
  }, [onNext]);

  const partnerNames = couple?.partners.map((p) => p.firstName) ?? [];

  return (
    <StepShell
      // The session card rides in the FOOTER, not in the scrolling content.
      //
      // Where it belongs, first: it is the one piece of standing fact on the
      // screen, and it reads as the answer to the button rather than the end of
      // the paragraph. Second, and the reason it moved: the card this flow sits
      // in has a 680px floor it cannot grow past (the shell's scroller is
      // `min-h-0`, so it shrinks instead of pushing the card taller), and the
      // welcome's content plus a ~100px session card overran that by about ten
      // pixels. Ten pixels of scroll is not a reading experience, it is a
      // gradient drawn over the last thing on the screen. Out of the scroller,
      // the content fits with room in both states.
      //
      // IN PRACTICE IT NEVER RENDERS HERE, and that is the point rather than an
      // oversight worth deleting the slot over. S1 is pre-bind, where
      // `coupleFromPreview` hard-codes `firstSessionAt: null` to withhold
      // person-bound data until a partner is picked, so there is no session to
      // show yet. The slot stays because the moment the preview carries one, or
      // this screen is reached bound, the card belongs exactly here.
      footer={
        <div className="flex flex-col gap-4">
          {/* Guarded on the SESSION, not just on `couple`. FirstSessionModule
              returns null without one, and an unguarded wrapper would still lay
              out: an empty animated div plus a 16px `gap-4` above the button,
              which reads as a missing element rather than as no element. */}
          {couple?.firstSessionAt && (
            // The two classes are mutually exclusive, not additive: both set
            // `animation`, so carrying `dova-rise` into the exit would leave the
            // entrance's 120ms delay on the departure and this one piece would
            // start leaving after everything else had gone.
            <div
              className={leaving ? "dova-sink" : "dova-rise"}
              style={leaving ? undefined : { animationDelay: `${BEAT_MS * 2}ms` }}
            >
              <FirstSessionModule
                firstSessionAt={couple.firstSessionAt}
                therapistName={couple.therapistName}
                durationMinutes={couple.firstSessionDurationMinutes}
                location={couple.firstSessionLocation}
                locationAddress={couple.firstSessionLocationAddress}
              />
            </div>
          )}
          <Button onClick={start}>Get started</Button>
        </div>
      }
    >
      {/* The pair sits at the TOP of the screen rather than inside the centred
          stack. With everything centred as one block, the whitespace all pooled
          above the pair and below the copy and the screen sat low; anchoring the
          pair up here splits that slack in two and gives the column something
          holding its top edge. */}
      <div
        className={`shrink-0 flex justify-center ${leaving ? "dova-sink" : "dova-rise"}`}
        style={leaving ? undefined : { animationDelay: "0ms" }}
      >
        <CouplePair names={partnerNames} />
      </div>

      <div
        className={`flex flex-1 min-h-0 flex-col items-center justify-center gap-4 text-center ${
          leaving ? "dova-sink" : ""
        }`}
      >
        <div
          // gap-2, the same step StepHeading puts between its title and its
          // first paragraph. These two paragraphs are one body of copy under one
          // heading, so a wider gap here made the second read as a separate
          // section that had lost its own heading.
          className="dova-rise flex flex-col gap-2"
          style={{ animationDelay: `${BEAT_MS}ms` }}
        >
          <StepHeading align="center" title="Welcome to Dova">
            <p className="max-w-[38ch]">
              A quiet companion to your couples therapy. Here to help you with
              admin and support between sessions, over text.
            </p>
          </StepHeading>

          <p className="text-base leading-[1.5] text-[var(--text-secondary)] max-w-[40ch]">
            Both you and your partner need to complete this onboarding
            separately. It takes less than five minutes.
          </p>
        </div>
      </div>

    </StepShell>
  );
}
